import './App.css';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { BrowserRouter } from 'react-router-dom';
import ProtectedRoutes from './Routes/ProtectedRoutes';
import axios from 'axios';

// Global Axios request interceptor to automatically attach authorization token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global Axios response interceptor for handling token expiration / 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const requestUrl = error.config?.url || '';
      // Skip redirection on public authentication/verification endpoints to preserve inline error responses
      const isAuthRoute = requestUrl.includes('/login') ||
        requestUrl.includes('/register') ||
        requestUrl.includes('/forgot-password') ||
        requestUrl.includes('/confirm-mobile-verification') ||
        requestUrl.includes('/send-mobile-verification');

      if (!isAuthRoute) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login?sessionExpired=true';
      }
    }
    return Promise.reject(error);
  }
);

const theme = createTheme({
  palette: {
    success: {
      main: "#4BCE97",
      background: "#EDFAF5",
    },
    primary: {
      main: "#6B31C4",
      light: "#8B5DD8",
      dark: "#5a29a5",
      background: "#F8F9FA",
      border: "#E5E7EB",
    },
    secondary: {
      main: "#6B7280",
      background: "#FFFFFF",
      textColor: "#9CA3AF",
    },
    error: {
      main: "#EF4444",
    },
    default: {
      main: "#374151",
      background: "#F3F4F6",
    },
    text: {
      primary: "#1A1A2E",
      secondary: "#6B7280",
    },
    background: {
      default: "#F8F9FA",
      paper: "#FFFFFF",
    },
    divider: "#E5E7EB",
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: ["'SF Pro Display'", "-apple-system", "BlinkMacSystemFont", "sans-serif"].join(","),
    fontSize: 14,
    fontWeightBold: 600,
    h1: { fontWeight: 700, fontSize: "2.5rem", letterSpacing: "-0.02em", color: "#1A1A2E" },
    h2: { fontWeight: 700, fontSize: "2rem", letterSpacing: "-0.01em", color: "#1A1A2E" },
    h3: { fontWeight: 600, fontSize: "1.5rem", color: "#1A1A2E" },
    h4: { fontWeight: 600, fontSize: "1.25rem", color: "#1A1A2E" },
    h5: { fontWeight: 600, fontSize: "1.1rem", lineHeight: 1.4, color: "#1A1A2E" },
    h6: { fontWeight: 600, fontSize: "0.95rem", lineHeight: 1.5, color: "#1A1A2E" },
    body1: { fontWeight: 400, fontSize: "0.875rem", lineHeight: 1.6, color: "#374151" },
    body2: { fontWeight: 400, fontSize: "0.8125rem", lineHeight: 1.5, color: "#6B7280" },
    button: { fontWeight: 500, fontSize: "0.875rem", textTransform: "none", letterSpacing: "0.01em" },
    caption: { fontWeight: 500, fontSize: "0.75rem", lineHeight: 1.4, color: "#9CA3AF" },
    overline: { fontWeight: 600, fontSize: "0.6875rem", lineHeight: 1.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9CA3AF" },
  },
  components: {
    /* ── Buttons: consistent across the app ── */
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: "150px",
          padding: "8px 20px",
          fontSize: "0.875rem",
          fontWeight: 500,
          minHeight: "40px",
          transition: "all 0.2s ease",
        },
        contained: {
          backgroundColor: "#6B31C4",
          color: "#FFFFFF",
          "&:hover": {
            backgroundColor: "#5a29a5",
          },
        },
        outlined: {
          borderColor: "#E5E7EB",
          color: "#374151",
          "&:hover": {
            borderColor: "#6B31C4",
            backgroundColor: "rgba(107, 49, 196, 0.04)",
            color: "#6B31C4",
          },
        },
        text: {
          color: "#6B7280",
          "&:hover": {
            backgroundColor: "rgba(107, 49, 196, 0.04)",
            color: "#6B31C4",
          },
        },
        sizeSmall: {
          padding: "4px 12px",
          fontSize: "0.8125rem",
          minHeight: "32px",
        },
        sizeLarge: {
          padding: "12px 28px",
          fontSize: "0.9375rem",
          minHeight: "48px",
        },
      },
    },
    /* ── Text Fields: consistent across the app ── */
    MuiTextField: {
      defaultProps: {
        size: "small",
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
            fontSize: "0.875rem",
            minHeight: "40px",
            "& fieldset": {
              borderColor: "#E5E7EB",
              borderWidth: "1px",
              transition: "border-color 0.2s ease",
            },
            "&:hover fieldset": {
              borderColor: "#D1D5DB",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#6B31C4",
              borderWidth: "1.5px",
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "0.875rem",
            color: "#1A1A2E",
            padding: "10px 14px",
            "&::placeholder": {
              color: "#9CA3AF",
              opacity: 1,
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
            color: "#6B7280",
            "&.Mui-focused": {
              color: "#6B31C4",
            },
          },
        },
      },
    },
    /* ── Selects: match text field styling ── */
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          fontSize: "0.875rem",
          minHeight: "40px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E5E7EB",
            borderWidth: "1px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#D1D5DB",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#6B31C4",
            borderWidth: "1.5px",
          },
        },
      },
    },
    /* ── Autocomplete: match text field styling ── */
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            fontSize: "0.875rem",
            "& fieldset": {
              borderColor: "#E5E7EB",
            },
            "&:hover fieldset": {
              borderColor: "#D1D5DB",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#6B31C4",
              borderWidth: "1.5px",
            },
          },
        },
      },
    },
    /* ── Cards: clean, flat ── */
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          backgroundColor: "#FFFFFF",
        },
      },
    },
    /* ── Paper: flat ── */
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: "12px",
        },
      },
    },
    /* ── Chips: consistent ── */
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          fontWeight: 500,
          fontSize: "0.8125rem",
          height: "28px",
        },
      },
    },
    /* ── Tables: clean ── */
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            backgroundColor: "#F8F9FA",
            color: "#6B7280",
            fontWeight: 600,
            fontSize: "0.8125rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            borderBottom: "1px solid #E5E7EB",
            padding: "12px 16px",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #F3F4F6",
          padding: "12px 16px",
          fontSize: "0.875rem",
          color: "#374151",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: "background-color 0.15s ease",
          "&:hover": {
            backgroundColor: "#FAFBFC",
          },
        },
      },
    },
    /* ── Dialogs: clean ── */
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    /* ── Links: brand color ── */
    MuiLink: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: "0.8125rem",
          color: "#6B31C4",
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      },
    },
    /* ── Tabs: minimal ── */
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.875rem",
          minHeight: "44px",
          color: "#6B7280",
          "&.Mui-selected": {
            color: "#6B31C4",
            fontWeight: 600,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: "#6B31C4",
          height: "2px",
        },
      },
    },
    /* ── Checkbox: brand color ── */
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#D1D5DB",
          "&.Mui-checked": {
            color: "#6B31C4",
          },
        },
      },
    },
    /* ── Radio: brand color ── */
    MuiRadio: {
      styleOverrides: {
        root: {
          color: "#D1D5DB",
          "&.Mui-checked": {
            color: "#6B31C4",
          },
        },
      },
    },
    /* ── Tooltips ── */
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#1A1A2E",
          fontSize: "0.75rem",
          borderRadius: "6px",
          padding: "6px 12px",
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <ProtectedRoutes />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
